/**
 * Central tuning. Every magic number that affects feel lives here so the game
 * can be balanced in one place instead of hunting through subsystems.
 */

export const TUNING = {
  /** Fixed simulation step budget. dt is clamped to this to survive tab-outs. */
  maxDeltaTime: 1 / 20,

  player: {
    /** Eye height standing, metres. */
    eyeHeight: 1.68,
    crouchEyeHeight: 0.95,
    /** Horizontal collision radius. */
    radius: 0.32,

    walkSpeed: 2.6,
    sprintSpeed: 5.0,
    crouchSpeed: 1.25,
    /** Multiplier while wading through Cell.Water. */
    waterSpeedScale: 0.62,

    /** Acceleration and braking, m/s^2. High values feel responsive. */
    accel: 34,
    decel: 42,
    /** Fraction of ground control retained while airborne. */
    airControl: 0.35,
    /** Fraction of ground braking that still applies with no feet on the floor. */
    airDrag: 0.06,
    /** Backpedalling is slower than advancing. */
    backwardScale: 0.78,

    jumpSpeed: 3.6,
    gravity: 18,

    /**
     * Half-width of a `Cell.Pillar`'s solid core as a fraction of cellSize.
     * The collision box and the pillar mesh must agree or the visual lies.
     */
    pillarHalfFraction: 0.2,

    /** Seconds to fully stand up or crouch down. */
    crouchTime: 0.16,

    /** Head bob. */
    bobFrequency: 8.4,
    bobAmplitude: 0.045,
    sprintBobScale: 1.5,
    /** Sideways sway coupled to the bob. */
    swayAmplitude: 0.028,
    /** Camera roll when strafing, radians. */
    strafeRoll: 0.021,

    /** Distance in metres between footstep sounds while walking. */
    stepInterval: 2.0,

    /** Look. */
    sensitivity: 0.0022,
    maxPitch: 85 * (Math.PI / 180),

    /** Metres. How far the interact ray reaches. */
    interactRange: 2.4,

    /** Noise the player emits, 0..1, used by entity hearing. */
    noiseCrouch: 0.08,
    noiseWalk: 0.4,
    noiseSprint: 1.0,
    noiseWaterBonus: 0.35,
    /**
     * Scenery shorter than this is stepped over rather than collided with, so
     * a carpet roll on the floor never catches the player's feet.
     */
    stepOverHeight: 0.42,
    /** Seconds of stillness before the player counts as hidden. */
    hideDelay: 0.6,
  },

  survival: {
    /**
     * Sanity. These three have to be read together against a real level's light
     * field. At 1.9 / 0.85 the bar emptied in under two minutes of ordinary
     * walking on every level and then stayed there for the rest of the run,
     * which turned every output keyed off it — whispers, hallucinations, the
     * post-fx, the faceling's approach — from a signal into
     * wallpaper. At 0.9 the unlit stretches still cost about a point a second,
     * so the dark is dangerous, but a lit room or the flashlight buys it back.
     */
    sanityDarkRate: 0.9, // per second in full darkness
    sanityLitRecovery: 1.7, // per second under a working light
    sanityRestRecovery: 2.6, // per second while crouched, still and lit
    /** Multiplier on entity-driven sanity drain when the entity is visible. */
    sanityVisibleScale: 2.4,

    /**
     * Health comes back on its own, but only once nothing has touched you for
     * `regenDelay` seconds — the gate is time out of trouble, not a supply you
     * have to keep topped up. At 0.35 a full bar takes about five minutes of
     * quiet, which is longer than most stretches between encounters, so a
     * bandage is still the thing you reach for mid-fight and waiting is the
     * thing you do afterwards.
     */
    regenDelay: 20,
    regenRate: 0.35,

    /** Below this, the HUD warns and audio adds a heartbeat. */
    criticalThreshold: 20,

    inventorySlots: 8,
  },

  flashlight: {
    /** Seconds of continuous use from a full charge. */
    fullDuration: 5.5 * 60,
    /** Charge restored by one battery, in points of 100. */
    batteryCharge: 55,
    /** Below this the beam starts browning out. */
    dyingThreshold: 18,
    coneAngle: 26 * (Math.PI / 180),
    range: 17,
    intensity: 2.6,
  },

  entities: {
    /** Metres. Beyond this an entity is put to sleep and stops pathing. */
    activeRadius: 46,
    /** Metres. Entities further than this may be recycled to a new spot. */
    despawnRadius: 80,
    /** Seconds between full A* repaths while chasing. */
    repathInterval: 0.45,
    /** Seconds an entity keeps hunting the last known position after losing the player. */
    searchDuration: 12,
    /** How much louder-than-ambient a noise must be to trigger investigation, 0..1. */
    noiseThreshold: 0.22,
    /** Metres the director keeps new spawns away from the player. */
    minSpawnDistance: 26,
    /**
     * Metres the ordinary spawn band reaches out to.
     *
     * It used to be `activeRadius * 0.95`, which put a spawn at up to 44 m —
     * outside the hearing of everything in the roster, so the thing arrived
     * knowing nothing and had to find the player by luck. Most of them never
     * did, and that is the single biggest reason nothing in this game used to
     * turn up. 38 m sits just inside a hunter's ears at a sprint and just
     * outside them at a walk, which is where the decision should be.
     */
    maxSpawnDistance: 38,
    /** Metres the director keeps new spawns away from the player's view cone. */
    minSpawnDistanceInView: 55,

    /**
     * The pursuit licence: what lets something keep coming.
     *
     * `activeRadius` exists because a player who has walked away from a
     * monster should be able to leave it behind — falling asleep wipes
     * awareness and the last known position, so a thing that lost you also
     * forgets you. That is right for something wandering. It is wrong for
     * something that has genuinely picked you up and set off, because on a
     * folded level a hunter that is thirty metres of corridor behind you is
     * often forty-seven metres of straight line away, and the old rule
     * cancelled the encounter mid-approach without either of you noticing.
     *
     * So an agent that is actually hunting is handed a licence
     * (`Agent.longHunt`) which raises its *own* sleep and recycle thresholds,
     * and nothing else's. It is a countdown rather than a flag so it cannot
     * leak: the entity system refreshes it every frame the agent spends in a
     * hunting mode, drops it the instant the engagement resolves or the agent
     * sleeps, and reverts to the ordinary radii the moment it hits zero.
     */
    pursuit: {
      /**
       * The kinds that earn a licence. This is a roster and not a speed
       * threshold, because "will it come at you" is a fact about a behaviour
       * and cannot be read off a number in `defs.ts`.
       *
       * The bar is that the thing *arrives*. A smiler at 1.15 m/s holds a dark
       * spot and backs away from anyone walking toward it; a clump cannot walk
       * at all; a hollow is not real. Licensing any of those would keep a thing
       * awake across a distance it was never going to cross, which is worse
       * than letting it sleep — it suppresses the director's pressure for the
       * whole time it is pretending to hunt.
       *
       * Every kind below closes: the hound by ear, the bacteria by running the
       * player down, the faceling every time it is not being watched, the
       * partygoers in a pack, the centipede through water, the deathmoth
       * straight at a lit torch. The deathmoth is the one that self-gates —
       * with the flashlight off it never enters a hunting mode at all, so it
       * never draws the licence.
       */
      kinds: ['hound', 'bacteria', 'faceling', 'partygoer', 'centipede', 'deathmoth'] as const,
      /**
       * Seconds a licence lasts once granted. Refreshed every frame the agent
       * is still hunting, so this is only ever the grace after it stops: long
       * enough to survive a lost second of awareness, short enough that
       * something which has genuinely given up falls asleep like anything else.
       */
      licence: 12,
      /**
       * Metres. A licensed hunter stays awake out to here rather than
       * `activeRadius`. Comfortably past the far end of the surge band even
       * down a dead-straight corridor, where the walk and the straight line are
       * the same number, plus room for a player who runs the other way for a
       * few seconds before thinking better of it.
       */
      activeRadius: 118,
      /**
       * Metres. And is not recycled inside here rather than `despawnRadius`.
       * Above `activeRadius` by the same margin the ordinary pair uses, so a
       * hunter that has just been put to sleep is not also deleted.
       */
      despawnRadius: 155,
      /**
       * The sweep: how a hunter with no idea where the player is finds them.
       *
       * Without it a spawn thirty-eight metres away is a coin flip that mostly
       * lands on "wandered the other way". With it, a hunter walks toward the
       * player's general area every `sweepInterval` seconds — but *only* while
       * the player is making a noise worth following. Below
       * `entities.noiseThreshold` nothing in the building hears you, and a
       * crouched player emits 0.08 against a threshold of 0.22, so going quiet
       * stops the sweep dead and it goes back to drifting. That is the same
       * counterplay the hound has always had, applied to the approach instead
       * of only to the last thirty metres.
       */
      sweepInterval: [7, 13] as [number, number],
      /**
       * Error in the swept-to point as a fraction of the distance, clamped.
       * The building-scale version of the surge's trail spread: it walks to
       * roughly the right room and its own ears have to do the rest, so this
       * can never become a homing beacon.
       */
      sweepError: 0.35,
      sweepErrorMin: 7,
      sweepErrorMax: 26,
    },

    /**
     * The long hunt: where a slide surge puts what it summons.
     *
     * A surge spawn is deliberately placed most of the way across the floor,
     * because the whole point is something the player *hears* crossing it
     * rather than something already in the room. It leans on the same licence
     * as `pursuit` above, for much longer.
     *
     * See `entities/surge.ts` for the trail that carries it and the cue track
     * that makes the approach audible.
     */
    surgeSpawn: {
      /**
       * Metres of *walking*, not straight-line distance: the placement search
       * measures a four-connected route from the player, which is the distance
       * the thing actually has to cover.
       *
       * Derived from how long the approach should take. A hound crosses ground
       * it is investigating at `walkSpeed * 1.55` = 2.8 m/s, and spends about
       * ten seconds on the endgame — the last thirty metres, where its own ears
       * pick the player up, it growls its lead, and then runs at 5.35 m/s
       * against a 5.0 m/s sprint. Twenty to thirty seconds of approach is the
       * window that felt right: long enough to hear it, place it and decide,
       * short enough that it is a race rather than an errand. That is 30-60 m
       * of walking before the endgame, so 60-90 m of band. Below fifty there is
       * no dread; past a hundred the player has usually left before it lands.
       */
      distance: [62, 90] as [number, number],
      /**
       * The kinds a surge may summon from across the floor, narrower than
       * `pursuit.kinds` by exactly one: the approach cue track is a promise
       * that something is coming, and only a thing that keeps that promise
       * *unconditionally* belongs here.
       *
       * Measured end to end — a player camping at a slide mouth until the
       * director placed something, 24 runs each, with all six of `pursuit.kinds`
       * temporarily in this roster so that every one of them got a surge
       * placement to be judged on. Placement median 56 m of straight line.
       * Fraction that then reached contact, and the median seconds of approach
       * once placed:
       *
       *     hound      20/24   28 s        centipede  20/23   27 s
       *     bacteria   20/24   23 s        partygoer  22/24   34 s
       *     faceling   19/24   18 s        deathmoth  24/24   26 s
       *
       * The deathmoth is the one left out, and its own number is why. That
       * 24/24 was with the player's torch on. With it off the same run is
       * **0 of 24** — it never commits, because a moth that is not being shown
       * a light has no reason to cross a floor. That is a good entity and a
       * terrible thing to build a cue track around: the surge would announce an
       * approach that a player who had simply not switched their torch on was
       * never going to receive. It keeps its pursuit licence, where the gate is
       * the point, and loses the narrated one.
       */
      kinds: ['hound', 'bacteria', 'faceling', 'partygoer', 'centipede'] as const,
      /**
       * Where along the player's own route to the slide a hunt spawn should be
       * able to *see*, as a fraction of the way there.
       *
       * A hunter that arrives behind the player through a wall is not drama; it
       * is a noise and then teeth. So a surge placement prefers a cell with a
       * clear line to the stretch of floor the player is about to walk, taken
       * at these two fractions of the route, and prefers to come from roughly
       * the direction they are already heading. The two together are what turn
       * an approach into something crossing a lit hall in front of them.
       *
       * Preferences and not requirements: a folded level may have nothing that
       * qualifies, and a hunt that arrives is worth more than one that is
       * refused for being badly framed.
       */
      routeSample: [0.45, 0.75] as [number, number],
      /**
       * Cosine of the widest angle off the player's bearing to the slide that
       * still counts as "ahead of them". 0.3 is about seventy-two degrees:
       * wide enough that a folded building can nearly always answer it, narrow
       * enough that the thing comes from the half of the world the player is
       * walking into rather than the half behind their shoulders.
       */
      routeCos: 0.3,
      /**
       * Light level a hunt spawn prefers to stand in. `render.lightLevelAt`
       * runs 0..1 and Level 0's lit halls sit around 0.5; below about a third
       * a body at forty metres is a smudge in the fog rather than a shape. It
       * is a preference, and it is checked alongside a light-shy kind's own
       * `lightAversion` rather than instead of it — a faceling wants 0.6 or
       * less and this wants 0.3 or more, and the band between them is exactly
       * the dim room a silhouette reads best in.
       */
      minLight: 0.3,
      /**
       * Seconds the licence lasts at `threatScale` 1, scaled by the level's
       * own. It only binds outside `pursuit.activeRadius`; once the hunter is
       * close the ordinary rules hold it there. Long enough to cover the
       * worst-case walk twice over, short enough that a hunt which never
       * connects ends. It is also released early the moment an engagement
       * resolves.
       */
      duration: 70,
    },

    /**
     * The pack: kinds that must never be met one at a time.
     *
     * The director's rule is that nothing accrues while something is already
     * engaged, which is what keeps the game quiet — and which structurally
     * prevents a second Partygoer from ever existing while the first is
     * stalking. Measured on the Funrooms over thirty minutes: something alive
     * for seventeen of them, and never more than one of it. "You can never
     * catch a Partygoer alone" is both the canon and the level's stated intent,
     * and the director was the thing making it false.
     *
     * So a pack kind gets a second route onto the floor. It is not the pressure
     * economy and it does not touch it: while one of them is actually working
     * on the player, the rest of the pack answers the scream — placed the
     * ordinary way, out of sight, at a fair distance, and told where to go the
     * same way `alertKin` tells a live one. Nothing else in the game changes,
     * because nothing else is in `kinds`.
     */
    pack: {
      /**
       * Kinds that hunt in numbers. Exactly one entry, and adding another is a
       * claim about that entity's documented behaviour rather than a difficulty
       * knob — a pack is a promise that the player cannot deal with one of them
       * at a time, and every kind here needs a behaviour that means it.
       */
      kinds: ['partygoer'] as const,
      /**
       * Bodies in a full pack. Three is what the sources describe and what the
       * player asked for; it is also as many as one of these can be worth
       * before a corridor stops being survivable.
       */
      size: 3,
      /**
       * How far a pack may exceed the level's `entityBudget`. The budget is a
       * cap on how much is happening at once, and a pack is one thing
       * happening; the Funrooms' own budget of 2 was authored to mean "it
       * brings company", so this is what makes the third body legal there and
       * nowhere the level has not already asked for two.
       */
      bonus: 1,
      /** Seconds after the first one commits before the rest start arriving. */
      first: 5,
      /**
       * Seconds between the ones after that, so they come in over half a minute
       * from wherever they were rather than appearing as a group.
       */
      interval: [9, 17] as [number, number],
      /**
       * How certain a called pack member is of where the player was. The same
       * 0.9 as `ENTITY_TUNING.partygoer.callLoudness`, because it is the same
       * scream: it is hearsay, so it still has to cross the floor and still has
       * to find the player at the end of it.
       */
      answerLoudness: 0.9,
    },

    /**
     * Body radius the shared route is planned for, metres. Slightly over the
     * widest thing that actually walks (a wretch at 0.50) so one plan serves
     * every kind; the per-agent radius is applied again by the string-pull,
     * which is where a route is fitted to the body that asked for it.
     */
    navRadius: 0.52,
    /**
     * Extra A* cost for a cell with scenery in it. A route through the gap
     * beside a bathtub is legal, it is just not the one to take when the room
     * next door is empty. Deliberately smaller than the detour it would buy, so
     * a body still squeezes past when squeezing past is genuinely the way.
     */
    propCost: 0.55,
    /**
     * Metres of halo around a prop's bounding circle used to flag cells for the
     * cheap "no scenery here" test. Must be at least the widest body that will
     * ever ask, or that test would answer for a body it never covered. A clump
     * is 0.85.
     */
    propFlagHalo: 1.0,
    /**
     * Getting a body past scenery it has wedged itself on. A body that gets
     * nowhere throws its route away and tries again; `snagLimit` failures
     * inside `1 / snagDecay` seconds means the route was never the problem, and
     * it is allowed through scenery for `propGrace` seconds. Long enough to
     * clear a piano at a walk, short enough that nobody sees a monster standing
     * inside one.
     */
    snagDecay: 0.25,
    snagLimit: 2,
    propGrace: 0.9,
  },

  director: {
    /**
     * Encounters are rare by design: mostly exploration and ambience.
     * Pressure accrues while the player is safe and is spent on a spawn.
     */
    pressurePerSecond: 1.0,
    /** Pressure needed before a spawn is even considered. */
    spawnThreshold: 150,
    /** Pressure refunded/withheld right after an encounter ends. */
    postEncounterCooldown: 95,
    /** Seconds of guaranteed calm at the start of a level. */
    gracePeriod: 75,
    /** Pressure multiplier while the player is making a lot of noise. */
    noisePressureScale: 1.7,
    /** Chance per eligible check that the director fires a harmless scare instead. */
    fakeoutChance: 0.62,
    /**
     * Seconds between ambient one-shots (distant thuds, scrapes). The pool is
     * two sounds, so at a 46-second mean they became furniture — roughly
     * nineteen of them over a level. Half as many, each worth twice as much.
     */
    ambientEventInterval: [38, 105] as const,

    /**
     * The slide surge: the building noticing that the player found the way
     * down. Everything above describes the whole game *except* somebody walking
     * at a slide; these change it there and nowhere else — a rate multiplier
     * inside `dwellRadius` of a mouth, and one hunter bought outright for
     * anyone who crosses `armRadius` and keeps closing. See
     * `entities/surge.ts` for the model they parameterise.
     */
    surge: {
      /**
       * Metres. The reach: inside this, walking at a slide starts the sequence.
       *
       * It used to be 17, which is the room the slide stands in — so the surge
       * was inert until the player was practically standing on the mouth, and
       * the whole approach happened after they had arrived. There was nowhere
       * for the drama to be.
       *
       * The number is derived from how long the sequence takes, against how
       * long the player takes to walk the rest of the way in at 2.6 m/s:
       *
       *   ~3 s   the roll. `approach.charge` below buys it almost at once, so
       *          this is a couple of seconds and not the two minutes an
       *          ordinary threshold crossing takes.
       *   ~12 s  the hunter closing far enough to be visible at all. It is
       *          placed 62–90 m of walking out (measured median 66 m of
       *          straight line) and closes at 2.4–2.8 m/s while investigating;
       *          it has to reach about forty metres, which is inside both
       *          Level 0's fog at 54 m and the entity system's 44 m sight cap.
       *   ~6 s   of actually watching it come, which is the point of the whole
       *          exercise and not a rounding error.
       *
       * 2.6 * (3 + 12 + 6) = 55 m. Rounded to 56: above the fog at 54 and well
       * inside the cull at 68, so the far edge of the band is still somewhere
       * the building is drawn and a hall can be seen down.
       *
       * This widens where the sequence *starts*. It deliberately does not widen
       * where the pressure multiplier bites — that is `dwellRadius` — because a
       * fifty-six metre circle of multiplied spawn rate around every mouth
       * would turn most of a floor into a surge.
       */
      armRadius: 56,
      /**
       * Metres of hysteresis on the reach. A player walking the edge of the
       * band must not arm and disarm it every few steps, since arming is what
       * decides whether a spawn is narrated as a hunt.
       */
      armHysteresis: 7,
      /**
       * Metres. The escalation radius, and what `armRadius` used to be: roughly
       * the room a slide stands in plus the way into it. Proximity — which
       * drives the pressure multiplier, the grace burn, the lure and the cue
       * track's intensity — runs 0 here and 1 at the mouth, so a player
       * crossing the far side of the floor still changes nothing at all.
       */
      dwellRadius: 20,
      /** Metres. At or inside this the player is at the mouth; proximity is full. */
      mouthRadius: 5,

      /**
       * The approach: what happens when someone is plainly walking at a slide.
       *
       * The rest of this block is a rate multiplier, and a rate multiplier
       * cannot promise anything. Measured on Level 0, a player walking in from
       * sixty metres had a monster in view before they reached the mouth in
       * 2 runs out of 24 — the surge armed six seconds before they arrived,
       * rolled nothing in that time, and the slide moment was empty. This is
       * the part that makes it a scene: heading for a slide opens a window, the
       * window buys one hunter across the floor, and the walk in is spent
       * listening to it come.
       *
       * It is not a siege. One hunt per window, one window per `cooldown`, and
       * the window only opens for someone who is actually closing on a mouth.
       */
      approach: {
        /**
         * Metres of ground the player must give up toward a mouth, inside the
         * band, before the building believes they are going there. Enough that
         * crossing the band on the way to somewhere else does not trip it — the
         * chord of a 56 m circle walked tangentially never closes twelve metres
         * — and short enough to be spent in the first few seconds of a real
         * approach.
         */
        commit: 12,
        /**
         * Metres of ground given back before the building stops believing it.
         * Above `commit` would make backing off free; well below it would let a
         * single doorway detour cancel an approach that is plainly happening.
         */
        slack: 9,
        /**
         * Pressure per second while the window is open, on top of the level's
         * own. The director's threshold is 150 and pressure sits anywhere in
         * [-95, 150] when the window opens, so this is sized to cross it from
         * the worst case in about three seconds — the first line of the
         * derivation on `armRadius`. Scaled by the level's `threatScale` like
         * every other pressure in this game, so the calmest floors spend most
         * of the window on it rather than the first breath.
         */
        charge: 85,
        /**
         * Seconds the window stays open. The charge crosses the threshold in
         * about three, so this is only the allowance for the director being
         * busy or the roll landing on a floor with no room for it. After that
         * it closes and the short cooldown applies.
         */
        window: 14,
        /**
         * Seconds before another approach may be summoned, and the fraction of
         * that a window which bought nothing has to wait.
         *
         * Divided by the level's `threatScale`, and that division is where a
         * calm floor's calm actually lives: the *first* time a player walks up
         * to a way down, every floor answers, because that moment is the one
         * the whole feature exists for. How relentless the building is about it
         * afterwards is the level's own business — 110 seconds on Level 1, 81
         * on Level 2, and more than five minutes on Level 94.
         */
        cooldown: 110,
        retry: 0.35,
        /**
         * Fakeout chance while the window is open. Nearly zero on purpose: the
         * cue track that starts here is a promise that something is coming, and
         * this is the one roll in the game that has to keep it. Every other
         * roll on the floor, including the ones the mouth's own escalation
         * buys, still runs at `fakeoutEarly`/`fakeoutLate`.
         */
        fakeout: 0.05,
      },

      /**
       * Seconds of standing at the mouth to reach full escalation. A player who
       * walks up, looks and jumps in gets a hint; a player who stands there for
       * a minute is in real trouble. At 45 the second and third rolls of a
       * stand-off land inside that minute.
       */
      rampFull: 45,
      /** Pressure multiplier at the mouth the instant the surge arms. */
      baseScale: 5,
      /** Extra pressure multiplier bought by lingering, on top of `baseScale`. */
      peakScale: 16,

      /**
       * Seconds of escalation lost per second out of range. Slower than it
       * built, so stepping out of the room for a moment does not wipe the
       * slate, but a full minute away is a clean reset.
       */
      dwellDecay: 0.75,
      /**
       * Pressure per second handed back once out of range. Surge pressure is a
       * loan: a player who looks at a slide, thinks better of it and walks away
       * must not carry a fully charged director around the rest of the floor.
       * At 4 a full threshold's worth is repaid in under forty seconds.
       */
      drainRate: 4.0,

      /**
       * How much faster `gracePeriod` burns at the mouth. Without it a player
       * who lands beside a slide would be immune for seventy-five seconds,
       * which is longer than the whole stand-off the surge is meant to create.
       * At 5 the mouth burns the full grace period in about twelve seconds.
       */
      graceBurn: 5,

      /**
       * Fakeout chance at the mouth when the surge has just armed, and when it
       * has fully escalated. Early the extra rolls should be nerve and nothing
       * else; late they should mostly be real. Blended toward
       * `director.fakeoutChance` by proximity, so nothing changes at the edge
       * of the radius.
       */
      fakeoutEarly: 0.86,
      fakeoutLate: 0.3,

      /** Seconds between surge one-shots at zero and at full intensity. */
      cueSlow: 12,
      cueFast: 4.2,
      /**
       * Metres the surge one-shot sounds from at zero and at full intensity.
       * The near end stays a room away: the point is something arriving, and a
       * "distant" noise two metres off reads as a bug rather than a threat.
       */
      cueFar: 30,
      cueNear: 10,
      /** Above this intensity a voice joins the pool. Something is coming, not falling. */
      cueVoiceIntensity: 0.6,
      /** Seconds a director noise buys the cue track, so the two never fire together. */
      cueAfterFakeout: 2.0,

      /**
       * Intensity above which the building starts handing the player's position
       * to whatever is already awake, and how often. This is what makes camping
       * at a slide genuinely bad rather than merely noisy — but it only ever
       * does what a footstep does. Reached after roughly half a minute at the
       * mouth.
       */
      lureIntensity: 0.72,
      lureInterval: 7.5,
      /** Loudness a lure is worth. Above `entities.noiseThreshold`, below a sprint. */
      lureLoudness: 0.55,

      /**
       * Intensity at which the once-per-run warning lands. The mechanic is
       * otherwise invisible — the player cannot see pressure — and an
       * escalation nobody can attribute is unfair rather than tense. The line
       * itself is `RECALL.slideSurge` in `survival/notes.ts`, with the game's
       * other teaching lines.
       */
      warnIntensity: 0.45,

      /**
       * The long hunt, presentation half. Where the summoned thing is placed
       * and how long it lives is `entities.surgeSpawn`; this is how the player
       * finds out about it.
       *
       * Everything here is keyed off one number, `approach`: 0 when the hunter
       * is `far` metres away and 1 at `near`. The cue track gets faster, louder,
       * lower and spatially closer as it climbs, so "something is coming" is
       * legible from the sound alone — a direction to look in and a rate of
       * closing — before anything is visible.
       */
      hunt: {
        /** Metres. `approach` runs 0 here and 1 at `near`. Spans the whole spawn band. */
        far: 90,
        near: 16,
        /**
         * Metres. Inside this the hunter's own growls and footfalls are the
         * sound of it, so the cue track stands down rather than talking over
         * the thing it was announcing. Set at the ordinary spawn distance: from
         * here in, a long hunt is just an encounter.
         */
        handover: 26,
        /**
         * Metres. Where the *trail* stands down, as opposed to the cue track.
         *
         * These used to be the same number and that was the single biggest
         * reason a summoned hunter never turned up: at twenty-six metres the
         * building stopped saying where the player was, and twenty-six metres
         * is outside every hunter's ears — a hound hears a walking player from
         * 20.7 m — so the last stretch was a coin flip and the thing walked to
         * where the player had been half a minute earlier. Measured arrival
         * across the five kinds: 20 of 60.
         *
         * Sixteen is inside a hunter's ears at a walk and outside them at
         * anything quieter, which is exactly where the hand-off belongs: the
         * building hands over at the distance the hunter's own senses can
         * finish the job for a player who is making a noise, and the trail is
         * gated on that noise anyway. A crouched player still gives it nothing
         * at any distance, so the counterplay is untouched.
         */
        trailHandover: 16,
        /** Seconds between approach cues, at `far` and at `near`. */
        cueSlow: 7.0,
        cueFast: 2.6,
        /**
         * Metres the approach cue is placed at, at `far` and at `near`. The
         * *bearing* is always the hunter's true bearing — that is the whole
         * point, the player has to be able to tell which way it is coming from
         * — but the distance is compressed into the audio engine's falloff
         * (`audio.maxAudibleDistance` is 42 m) so something ninety metres off
         * is audible at all. The map is monotone and never places the sound
         * further than the hunter really is, so nearer always sounds nearer.
         */
        trackFar: 34,
        trackNear: 8,
        /**
         * Seconds between trail updates at a walk and at a sprint, at
         * `threatScale` 1. The trail is the building telling the hunter roughly
         * where the noise came from; a sprinting player refreshes it more than
         * twice as often as a walking one, and a crouched player not at all.
         */
        trailSlow: 6.5,
        trailFast: 2.6,
        /**
         * Metres of error in the trail at the far end, falling to nothing as it
         * closes. The building knows which room; the hound's own ears have to
         * finish the job. Without this the trail would be a homing beacon and
         * standing still would stop mattering.
         */
        trailSpread: 9,
        /**
         * Loudness the hand-off at spawn is worth. Above the hound's
         * `commitAwareness * (1 / 0.55)` would make it charge blind, so it sits
         * below that: enough to get it walking and searching, not enough to
         * commit. It commits when it can actually hear the player.
         */
        wakeLoudness: 0.7,
        /**
         * Seconds of licence a hunt is topped back up to whenever it is closer
         * to the player than it has ever been.
         *
         * `entities.surgeSpawn.duration` is a fixed budget for the whole
         * approach, and on a level with a `threatScale` under one it is shorter
         * than the walk it was granted for: Level 0 scales 70 s down to 49, and
         * a partygoer at 1.9 m/s needs more than that to cross sixty-six metres
         * of folded building. The licence expired mid-walk, the sleep rule
         * cancelled the hunt, and the player never learned there had been one.
         *
         * Renewing on progress fixes the cause rather than inflating the
         * budget, and it says something truer: a hunt that is still closing
         * keeps its licence, and one that has stalled for this long loses it
         * and falls asleep like anything else.
         */
        progressLicence: 15,
        /** Metres of ground that count as progress, so noise cannot renew it. */
        progressStep: 1.5,
      },

      /** Ceiling on the dread the surge alone may put into the audio layer. */
      tensionCap: 0.8,
      /**
       * Floor on intensity the moment the surge arms, as a fraction. Proximity
       * alone is worth something: crossing the radius should be audible before
       * any lingering has happened, or the first warning a player gets is a
       * spawn.
       */
      intensityFloor: 0.35,
    },
  },

  render: {
    defaultFov: 75,
    near: 0.05,
    far: 120,
    /** Base target for the adaptive resolution scaler. */
    targetFps: 60,
    /** Max ceiling lights kept as real THREE lights at once; the rest are emissive only. */
    maxDynamicLights: 12,
    /** Metres. Chunks further than this are hidden. */
    cullDistance: 68,
    /**
     * Hard ceiling on a level's own `LevelSpec.viewDistance`, in metres.
     *
     * `Infinity` on desktop, so nothing is capped and every level gets the view
     * it asked for — this exists only so an immersive session can pull Level
     * 94's 118 m and Level 11's 92 m down to something a headset can afford to
     * draw twice, without editing either level's spec. `render/xrquality.ts`
     * writes it on entry and restores it on exit.
     *
     * Intended reader is `render/renderer.ts`, where the cull boundary is
     * resolved: `Math.min(spec.viewDistance ?? cullDistance, viewDistanceCap)`.
     */
    viewDistanceCap: Infinity,
    /** Cell chunk size for geometry batching. */
    chunkCells: 8,
    /** ACES exposure. Fluorescent panels blow out without a shoulder. */
    toneMappingExposure: 1.12,
    /**
     * Fog far plane as a fraction of `cullDistance`. Below 1 so a chunk is
     * already fully fogged out by the time it is switched off. This wins over
     * `LevelSpec.fogFar` wherever the spec asks for more than the cull allows.
     */
    fogFarScale: 0.92,
    /**
     * Multiplier on the level's fog colour. Below 1 so distance darkens rather
     * than hazes; at 1 a long sightline ends in a bright wall of the level's
     * own colour, which is the opposite of the effect wanted.
     */
    fogDarkness: 0.42,
    /**
     * Ceiling fixture candela per square metre of ceiling height, so floor
     * brightness comes out the same in a 2.5m pipe and a 4.6m warehouse.
     */
    fixtureIntensityScale: 1.05,
    /** Metres a single fixture is considered to light, for `lightLevelAt`. */
    lightInfluenceRadius: [7, 15] as [number, number],

    /**
     * Entities that drain light. See `render/lightdrain.ts` for which kinds and
     * why; these are the numbers that decide how the effect *feels*.
     *
     * `lightDrainRadius` is the one that matters, and it is not the distance at
     * which the player sees anything. The smoothstep falloff in `lightdrain.at`
     * gives away most of the outer half — a fixture 24 m out is dimmed 20%,
     * which reads as nothing — so the *visible* edge of the dark sits around
     * 20 m from the source. Measured on Level 0 that puts the first visible
     * change at a smiler 30 m away and the first legible silhouette at about
     * 22 m: a lead of a few seconds, not the fogbank the radius implies.
     * Raising it widens the hole in the ceiling as well as pushing the warning
     * out, which is a feel decision — see `render/lightdrain.ts`.
     */
    lightDrainRadius: 34,
    /**
     * Deepest a fixture can be pulled down, 0..1. Short of 1 on purpose: a
     * player who cannot find the way out of a drained room is soft-locked, not
     * scared, so every tube keeps a sliver of strike in it.
     */
    lightDrainDepth: 0.88,
    /**
     * How much of the level's flat ambient the drain is allowed to take, 0..1.
     * Lower than the fixture depth because ambient is the floor the player
     * navigates by on levels lit from nowhere in particular (Level 4).
     */
    lightDrainAmbientDepth: 0.6,
    /**
     * Drain and recovery rates, per second, for the exponential damp on each
     * fixture. Recovery is far slower than the drain and is staggered per
     * fixture on top of this, so a room comes back raggedly rather than
     * switching on.
     */
    lightDrainRate: 1.7,
    lightRecoverRate: 0.3,
    /**
     * Extra seconds a fixture stays out after the dark has passed, spread per
     * fixture by hash. `[min, max]`.
     */
    lightRecoverHold: [0.4, 5.5] as [number, number],
    /**
     * Deepest the player's own torch browns out inside a drain, 0..1 of its
     * nominal beam. The torch failing is the strongest single beat in the
     * effect and also the most dangerous: it recovers on its own the moment the
     * drain lets up, and it never reaches zero.
     */
    lightDrainTorchDepth: 0.55,

    /**
     * What the picture gives up while the game is being *worn*.
     *
     * Applied by `render/xrquality.ts` on session entry and put back on exit;
     * nothing here is read on desktop. The shape of the problem is that a
     * headset is not one screen at 60 Hz, it is two at 72–90 Hz drawn by a
     * phone-class GPU, and three's WebGL renderer has no multiview — every
     * object in the render list is submitted once per eye. So both halves of
     * the budget have to come down: draw calls, because they double, and
     * fragment cost, because there are three to five times as many pixels.
     *
     * The numbers below are reasoned from the code and from the levels'
     * own specs, not measured on hardware. See the header of
     * `render/xrquality.ts` for the arithmetic and for what is still unproven.
     */
    xr: {
      /**
       * Multiplier on tone-mapping exposure inside a session. A headset shows
       * this game noticeably darker than a monitor does, and the tier's smaller
       * dynamic light pool costs a little more on top.
       */
      exposureLift: 1.45,
      /**
       * Metres. Replaces `cullDistance` for the session, which also shortens
       * the scenery radius — `Renderer.cullProps` reads the same number and
       * scenery is the worst term on Level 94's 1,644 props. 44 m is not a
       * guess: it is the draw distance this game shipped with until the
       * sightlines were lengthened, so it is a known-good frame that the
       * levels were dressed against. Costs the long corridor view, which is
       * real; buys back rather more than half of the level's draw calls.
       */
      cullDistance: 44,
      /**
       * Metres. Ceiling on a level's authored `viewDistance`. Level 94 asks
       * for 118 m and Level 11 for 92; those are the two levels with a skyline
       * worth keeping some of, so they are capped rather than flattened to 44.
       */
      viewDistanceCap: 64,
      /**
       * Fog closes earlier than the 0.92 desktop uses. With the cull this
       * tight the margin between "fully fogged" and "switched off" has to be
       * wider, or chunks blink out inside a view the player can still see —
       * and a pop in a headset is a pop in both eyes at head speed.
       */
      fogFarScale: 0.86,
      /**
       * Real lights in the pool, down from 12. This is the single biggest
       * fragment-shader lever in the game: `render/lights.ts` keeps every slot
       * in the scene at zero intensity rather than removing it (removing one
       * recompiles every material), so a forward shader pays for all twelve on
       * every pixel of every surface whether they are lit or not. Six is the
       * same budget the existing `low` quality tier uses, so it is a lighting
       * look the game already ships.
       *
       * Only ever applied at a level build. Changing the light count mid-frame
       * recompiles every shader, which in a session is a multi-second freeze.
       */
      maxDynamicLights: 6,
      /**
       * Eye-buffer scale, 1.0 being the browser's own recommendation for the
       * device. three can only set this before `requestSession`, so it is the
       * one resolution knob a session cannot change once it has started —
       * which is why the graphics quality the player already chose picks it.
       *
       * These are the values `render/xr.ts` was using as a local constant; it
       * asked for them to live here, so here they are. Read one and hand it to
       * `renderer.xr.setFramebufferScaleFactor` before the session starts.
       *
       * A warning attached to `high`: at 1.0 a Quest 3 hands out something like
       * 2064x2208 an eye, 9.1 Mpx a frame, 656 Mpx/s at 72 Hz. That is roughly
       * five times the fill of the desktop view on a fraction of the hardware,
       * and the arithmetic behind the rest of this tier does not close at it.
       * `medium` is where a standalone headset should start.
       */
      framebufferScale: { low: 0.7, medium: 0.85, high: 1 } as Record<'low' | 'medium' | 'high', number>,
      /**
       * Extra floor under fixed foveation, 0..1. Zero: the session's own
       * choice stands.
       *
       * three defaults foveation to maximum and `render/xr.ts` asks for
       * maximum explicitly, so today the periphery is already as cheap as it
       * can be and this has nothing to add. It exists so that if foveation is
       * ever pulled back for image quality, the governor has a lever again —
       * it may climb from whatever the session set, and never sinks below it,
       * because a governor that lowers foveation is one that makes the frame
       * more expensive. With no headroom, the governor's first real cut is the
       * post overlay instead.
       */
      foveation: 0,

      /* -------------------------------------------------------------- *
       * The post chain, which in a session is a blend-only overlay.
       * `render/postfx.ts` explains why. These scale what survives.
       * -------------------------------------------------------------- */

      /**
       * Grain amplitude as a fraction of the desktop pass. Below 1 because the
       * overlay mixes toward the noise instead of adding a signed offset, so
       * in the dark it reads stronger than the same number does on a monitor,
       * and because fine head-locked noise is the pattern most likely to
       * confuse a headset's reprojection.
       */
      grainScale: 0.6,
      /**
       * How much of the sanity grade the overlay applies. The overlay cannot
       * read the frame, so it cannot desaturate; it mixes toward a sour olive
       * instead, and a flat mix at the desktop's 0.6 would read as a filter
       * laid over the room rather than the room going wrong.
       */
      sanityGrade: 0.45,
      /** Single-frame dropouts, scaled down: a full black frame in a headset. */
      dropoutScale: 0.45,
      /**
       * Comfort vignette, in the post overlay. **Zero, deliberately.**
       *
       * `render/xrvignette.ts` owns comfort: it draws a proper angular tunnel
       * parented to the camera, driven by what the player is actually doing,
       * which is a better instrument than a screen-space ring and is already
       * wired to locomotion. Two darkenings stacked would be one too many, so
       * the overlay's version stays off.
       *
       * It is kept rather than deleted because it is three lines of shader
       * that already exist and it is the fallback if the tunnel is ever taken
       * out. Raise `moving` and feed `setXRComfortMotion` to turn it on.
       */
      comfortVignette: 0,
      comfortVignetteMoving: 0,

      /* -------------------------------------------------------------- *
       * The frame-rate governor.
       * -------------------------------------------------------------- */

      /** Frames per sample window. ~1.2 s at 72 Hz. */
      governorWindow: 90,
      /**
       * Fraction of a window that may be late before quality steps down. A
       * headset frame is vsync-locked, so a late frame is a *dropped* frame
       * and the interval jumps to twice the refresh; 6% of a window is enough
       * to be a pattern rather than a level load.
       */
      governorDropFraction: 0.06,
      /** An interval longer than this many refresh periods counts as dropped. */
      governorLateFactor: 1.5,
      /** Clean windows in a row before quality is allowed back up. */
      governorRecoverWindows: 4,
      /**
       * Hz the governor asks the session to drop to when it has run out of
       * quality to cut. Better a solid 72 than a ragged 90.
       */
      governorFloorHz: 72,

      /* -------------------------------------------------------------- *
       * Pointing at things. `render/xrinteract.ts` picks with these.
       *
       * Every number here was reasoned from geometry rather than felt in a
       * headset, and they are the ones most likely to want moving once
       * somebody has worn one. They are here rather than as constants in that
       * file for exactly that reason.
       * -------------------------------------------------------------- */

      /**
       * Metres a ray may reach a supply at. Two grid cells.
       *
       * Much longer than the flat game's 2.4 m, and deliberately: 2.4 m is an
       * arm and a step, which is the right shape when crossing the floor costs
       * you nothing but walking. In a session crossing the floor costs a
       * teleport hop, and a supply you can see but must travel to and then aim
       * at again is an errand rather than a find.
       */
      interactReach: 6,
      /**
       * Metres a ray may reach a way *down* at.
       *
       * Half the rest, because descending is the only unrecoverable act in a
       * run and it shares its button with locomotion. A stray pinch near a
       * doorway must not drop the player a floor.
       */
      exitReach: 3,
      /** Height and target size of a supply lying on the floor, metres. */
      itemAimY: 0.16,
      itemAimRadius: 0.3,
      /** Height and target size of a way down: a mouth in a wall, roughly. */
      exitAimY: 0.7,
      exitAimRadius: 0.9,
      /**
       * Angular slop added to a target's own radius, as a fraction of the
       * range to it — so a bottle is as easy to hit at six metres as at two.
       * 0.05 is about a 2.9° half-angle.
       */
      aimAngleSlop: 0.05,
    },
  },

  audio: {
    masterVolume: 0.8,
    /** Metres at which a positional sound is fully attenuated. */
    maxAudibleDistance: 42,
    /** Reference distance for the inverse falloff. */
    refDistance: 3.5,
    /** Fluorescent hum base frequency. */
    humFrequency: 120,
    /** Seconds for the ambience bed to cross-fade on a level change. */
    ambienceFade: 2.5,
  },

  world: {
    /** Default metres per grid cell. Levels may override. */
    cellSize: 3.0,
    /** Wall thickness as a fraction of cellSize, for the pillar/wall meshes. */
    wallThickness: 1.0,

    /**
     * The slides — the way down the game actually points at. All of these are
     * metres of *walking* rather than cells, because that is the unit the
     * player pays in: the same hundred metres is thirty-three cells of the
     * Lobby and thirty-eight of Pipes, and it is the hundred metres they feel.
     * See `world/gen/slides.ts` for how they are applied.
     */
    slides: {
      /**
       * `coverageTarget` is the one that decides how many there are, and it is
       * a target for the *level*, not for the slide: mouths are added until the
       * mean walk from an arbitrary cell of the floor to the nearest one falls
       * under it. So the count comes out of the level's size and shape rather
       * than being authored. 130 m is about fifty seconds at a walk if you go
       * straight there, and two to three minutes of the wandering nobody does
       * in a straight line.
       */
      coverageTarget: 130,
      /**
       * Bounds on the count. Below two, half of a big level has no way down
       * near it at all; past five the level stops being a building you are lost
       * in and starts being a corridor with exits off it. The ceiling is rarely
       * what binds — a sixth slide buys the Lobby 8% and Pipes 3%.
       */
      minCount: 2,
      maxCount: 5,
      /**
       * Two slides closer than this are one slide with a spare: the second does
       * nothing for the half of the map that has neither.
       */
      separation: 110,
      /**
       * The first stretch of a level is meant to be plain empty backrooms —
       * arriving within sight of the way out is the one thing that would make a
       * slide feel like a handout. Measured to the mouth, the way the player
       * would have to walk it, and a floor rather than a target: the nearest
       * slide to the spawn averages about 150 m.
       */
      spawnClearance: 80,
      /**
       * Ceiling on the bonus a candidate gets for being far from every slide
       * already placed. Without a cap the far corner of the map wins on
       * distance alone; with it, "somewhere else entirely" is worth about two
       * and a half times "somewhere good", which is enough to move it across
       * the level and not enough to put it somewhere nobody would look.
       */
      spreadCap: 2.5,

      /**
       * The signage, which is what actually gets a player to a slide.
       *
       * The fog closes at thirty-four metres and the building is made of rooms,
       * so a mouth is visible from about a twentieth of the floor however many
       * of them there are. Simulated walks bear that out: with four arrows
       * clustered around each mouth, a thorough sweep of Level 0 took five
       * minutes to lay eyes on the first slide and a wandering player eleven.
       * Marks spread over the whole floor are the only thing that reaches the
       * other nineteen twentieths.
       *
       * Metres across of the district each mark serves: the level is cut into
       * squares this big and each one gets the best wall in it, so the coverage
       * is uniform and the count falls out of the level's size the way the
       * number of mouths does.
       */
      signSpacing: 30,
    },
  },

  /**
   * The on-screen controls, for a machine played with thumbs.
   *
   * The feel here is deliberately not a shooter's. The game is slow, the rooms
   * are large and most of a run is walking, so look is geared low, the stick
   * has a wide deadzone, and running is something you lean into rather than a
   * button you hold — a held sprint costs a finger the player does not have
   * spare. Read only by `core/touch.ts`; every one of these is a guess that
   * wants ten minutes on a real iPad before it is believed.
   */
  touch: {
    /** Stick travel in CSS pixels: a fraction of the short screen edge, clamped. */
    stickMinRadius: 52,
    stickMaxRadius: 92,
    stickRadiusFraction: 0.105,
    /**
     * Fraction of full travel that still counts as "not pushed".
     *
     * Wide on purpose. The player controller normalises the wish vector, so any
     * push at all is full walking speed — an eighth of a millimetre of thumb
     * drift while resting is the difference between standing in a corridor and
     * walking into a wall.
     */
    deadzone: 0.2,
    /** Push past this much of full travel to break into a run… */
    sprintOn: 0.92,
    /** …and fall back below this to stop. The gap is what stops it flickering. */
    sprintOff: 0.72,
    /**
     * Multiplier on the configured mouse sensitivity for a finger drag.
     *
     * At the default 0.0022 rad/px this turns about 180° over a nine-hundred
     * pixel drag: one and a half sweeps of the right half of an iPad. A shooter
     * would do it in one, and would be unusable in a game where the threat
     * model is listening rather than aiming.
     */
    lookGain: 1.6,
    /** Pitch, relative to yaw. Slightly slower; the axis is only ±85° wide. */
    pitchGain: 0.9,
  },

  ui: {
    /**
     * The descent, in seconds. Generating and meshing a floor is a synchronous
     * 100–400 ms freeze; as a hitch it reads as a bug, so the screen is taken
     * away first and the work is done behind it. The whole sequence runs on the
     * wall clock in `game.ts` — the simulation is halted for all of it — and the
     * generation lands at the start of `descendHold`, where there is nothing on
     * screen to be late for. The hold is also what makes the level card land on
     * black instead of over the floor you just arrived on.
     */
    descendFadeOut: 0.55,
    descendHold: 0.8,
    descendFadeIn: 1.15,

    /**
     * The noise readout. `PlayerState.noise` runs 0.08 crouching, 0.4 walking
     * and 1.0 sprinting, so `noiseShow` sits above a walk: the row appears only
     * when the player does something genuinely loud, which is how they find out
     * what "loud" means before a hound makes it matter. See `ui/hud.ts`.
     */
    noiseShow: 0.55,
    /** Noise at or above this, with something hunting, turns the row red. */
    noiseLoud: 0.34,
    /** Seconds the noise row lingers after the player goes quiet. */
    noiseHold: 3.0,
    /** Metres. Something hunting inside this forces the noise row on. */
    noiseHuntRange: 34,
  },

  /**
   * The shared world, on the client side.
   *
   * Only the numbers that are *feel* live here. The wire numbers — how often a
   * position goes out, how long the server waits before dropping somebody, how
   * far a strike may reach — are in `net/protocol.ts`, because they are a
   * contract with the server rather than a knob, and a build that disagreed
   * with the Worker about `STATE_HZ` would not be mistuned, it would be wrong.
   *
   * All of this is inert when there is no server. See `net/client.ts`.
   */
  net: {
    /**
     * The strike, which is a wind-up rather than a button: you have to hold the
     * crosshair on a specific person's chest for `strikeWindUp` seconds while
     * standing next to them, and they watch a ring close on their own body for
     * most of a second first. Eight hits to put somebody down — long enough
     * that killing a friend takes about ten seconds of chasing them and meaning
     * it, and far too long to happen by accident.
     */
    strikeWindUp: 0.45,
    strikeCooldown: 0.9,
    strikeDamage: 13,
    /**
     * Metres. Deliberately short of `STRIKE_RANGE`: the server checks against
     * the attacker's *last reported* position, which is up to a frame of
     * movement behind where the attacker thinks they are, and a strike that
     * looked like a hit and was refused is worse than one that never started.
     */
    strikeRange: 2.2,
    /** Metres from the aim ray the target's chest must be. A chest, aimed at. */
    strikeAim: 0.45,

    /**
     * How many remote torches may cast a real light. Each one is a live
     * `SpotLight` and therefore a shader recompile the first time it appears,
     * so this is a small number and zero on low quality — the glow at the lens
     * remains either way, which is what actually reads across a room.
     */
    torchLights: 2,
  },
};
// Deliberately not `as const`: values widen to `number` so systems can copy them
// into mutable fields without literal-type friction.

/** Storage key for the persisted save blob. */
export const SAVE_KEY = 'backrooms.save.v1';
export const SAVE_VERSION = 1;
